import { Button, Flex, Switch, Text } from '@mantine/core';
import { useState } from 'react';
import {
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import { PreviewFiles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/ReviewFiles/PreviewFiles';
import { Declarations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/Declarations';
import { RightPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/RightPanel';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { SelectedTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useOriginalUploadToggle } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/useOriginalUploadToggle';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useDownloadedFileBlob } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlob';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { getValidFileNameForDownload } from '@dealstack/shared/lib/common/file/file';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	PreviewBody,
	PreviewHeaderBar,
	PreviewHeaderControls,
	PreviewHeaderPageBox,
	PreviewWrapper,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/styled';

interface Props {
	currentInvestor?: GetKYCInvestorInstanceRes;
	currentStep: Step;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	filesForStep: DataKYCFileTctWithFile[];
	isReadOnly?: boolean;
	selectedTemplate?: SelectedTemplate;
	kycSetting?: KycWorkflowSettingResponseDto;
}

export const StepContent = ({
	currentInvestor,
	currentStep,
	selectedKycFile,
	filesForStep,
	isReadOnly = false,
	selectedTemplate,
	kycSetting,
}: Props) => {
	const { dealId } = useRequiredParams('dealId');
	const tCommonUi = useTranslationCommonUi();
	const tMipManage = useTranslationMipManage();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [scale, setScale] = useState(1);

	const isDeclarationsStep = currentStep?.type === StepType.DECLARATIONS;
	const isFormStep = currentStep?.type === StepType.FORM;

	// Forms preview their DocumentWorkflow file (submitted upload, else blank template); other steps use the KYCFile.
	const { canViewOriginal, isViewingOriginal, originalFileId, setIsViewingOriginal } =
		useOriginalUploadToggle(selectedKycFile);
	const previewFileId =
		originalFileId ??
		selectedKycFile?.fileId ??
		(isFormStep
			? selectedTemplate?.fileId ?? selectedTemplate?.templateFileId ?? undefined
			: undefined);

	const { data: file, isLoading: isFileLoading } = useDownloadedFileBlob({
		fileId: previewFileId ?? '',
		dealId,
	});

	const fileName =
		selectedKycFile?.file?.name ??
		(isFormStep ? selectedTemplate?.fileName ?? selectedTemplate?.templateName ?? '' : '');
	const isEmptyState =
		!isDeclarationsStep &&
		(isFormStep ? !selectedTemplate && !selectedKycFile : filesForStep.length === 0);
	const showHeader =
		!isDeclarationsStep &&
		!isEmptyState &&
		(isFormStep ? !!previewFileId : !!selectedKycFile && !selectedTemplate);
	const isPdf =
		isFormStep && !selectedKycFile
			? true
			: (file?.fileExtension ?? selectedKycFile?.file?.extension) === FileExtension.pdf;

	const onDownload = () => {
		if (!file?.blob) {
			return;
		}
		const safeName = getValidFileNameForDownload(
			fileName,
			(file.fileExtension ?? FileExtension.pdf) as FileExtension
		);
		downloadFileFromBlob(file.blob, safeName);
	};

	if (isDeclarationsStep) {
		return (
			<PreviewWrapper>
				<PreviewBody>
					<Flex miw="70%" h="100%">
						<Declarations
							currentInvestor={currentInvestor}
							kycSetting={kycSetting}
							isReadOnly={isReadOnly}
						/>
					</Flex>
					<Flex w="100%" miw="30%" h="100%">
						<RightPanel
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							filesForStep={filesForStep}
							isReadOnly={isReadOnly}
							selectedTemplate={selectedTemplate}
						/>
					</Flex>
				</PreviewBody>
			</PreviewWrapper>
		);
	}

	return (
		<PreviewWrapper>
			{showHeader && (
				<PreviewHeaderBar>
					<Text size="B5Bold" truncate lineClamp={1}>
						{fileName || tMipManage('kyc.review.no_documents')}
					</Text>
					<PreviewHeaderControls>
						{canViewOriginal && (
							<Switch
								checked={isViewingOriginal}
								onChange={(event) =>
									setIsViewingOriginal(event.currentTarget.checked)
								}
								label={tMipManage('kyc.review.certification.view_original', {
									defaultValue: 'View original upload',
								})}
								data-testid={TEST_ELEMENT_IDS.KYC_REVIEW_VIEW_ORIGINAL_TOGGLE}
							/>
						)}
						{!!file?.blob && (
							<Button
								variant="subtle"
								color="dark"
								size="md"
								onClick={onDownload}
								leftSection={<Icon variant="download-v3" size="lg" />}
							>
								{tCommonUi('preview_pdf.download')}
							</Button>
						)}
						{isPdf && (
							<PreviewHeaderPageBox>
								<Text size="md">{tCommonUi('preview_pdf.page')}</Text>
								<Text size="md" fw={600}>
									{currentPage}
								</Text>
								<Text size="md">/ {totalPages}</Text>
							</PreviewHeaderPageBox>
						)}
						<Flex gap="0.5rem" align="center">
							<Icon
								variant="minus"
								size="lg"
								onClick={() => setScale((s) => Math.max(0.4, s - 0.2))}
							/>
							<Icon
								variant="plus"
								size="lg"
								onClick={() => setScale((s) => s + 0.2)}
							/>
						</Flex>
					</PreviewHeaderControls>
				</PreviewHeaderBar>
			)}
			<PreviewBody>
				<Flex miw={isEmptyState ? '100%' : '70%'} h="100%">
					<PreviewFiles
						currentStep={currentStep}
						currentInvestor={currentInvestor}
						selectedKycFile={selectedKycFile}
						filesForStep={filesForStep}
						selectedTemplate={selectedTemplate}
						fileBlob={file?.blob}
						fileExtension={file?.fileExtension}
						isFileLoading={isFileLoading}
						scale={scale}
						onCurrentPageChange={setCurrentPage}
						onTotalPagesChange={setTotalPages}
					/>
				</Flex>
				{!isEmptyState && (
					<Flex w="100%" miw="30%" h="100%">
						<RightPanel
							currentInvestor={currentInvestor}
							currentStep={currentStep}
							selectedKycFile={selectedKycFile}
							filesForStep={filesForStep}
							isReadOnly={isReadOnly}
							selectedTemplate={selectedTemplate}
						/>
					</Flex>
				)}
			</PreviewBody>
		</PreviewWrapper>
	);
};
